import moment from 'moment'
import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import * as yup from 'yup'

import { DealsDealStatusChoices } from '@/apollo/graphql'
import {
  Button,
  DatePicker,
  Form,
  FormButtons,
  OptionSelector,
  TextInput,
} from '@/components'
import { useApp, useForm } from '@/hooks'
import { t } from '@/i18n'
import CustomerSelector from '@/pages/Customer/customerSelector'
import { IDeal } from '@/types'

import CarSelector from '@/pages/Car/carSelector'
import CarSubModelSelector from '@/pages/Car/carSubModelSelector'
import { useBuildData } from '@/pages/Deals/utils'
import ProposalPriceFormComponents from '@/pages/Proposal/FormComponents/ProposalPriceFormComponents'
import { useSelect } from './useSelect'

interface Props {
  loading: boolean
  isEditing?: boolean
  submitting: boolean
  onCancel: () => void
  initialData?: Partial<IDeal>
  onSubmit: (d: IDeal) => void
}

const DealForm: React.FC<Props> = ({
  loading,
  onCancel,
  onSubmit,
  isEditing,
  submitting,
  initialData,
}) => {
  const { isRTL, currency } = useApp()

  const schema = useMemo(
    () =>
      yup
        .object({
          leaseDuration: yup
            .number()
            .required(t('required_field_error'))
            .max(2147483647),
          status: yup
            .mixed<DealsDealStatusChoices>()
            .oneOf(
              Object.values(DealsDealStatusChoices),
              t('required_field_error'),
            ),
          startDate: yup
            .date()
            .required(t('required_field_error'))
            .typeError(t('invalid_date_error')),
          endDate: yup
            .date()
            .required(t('required_field_error'))
            .typeError(t('invalid_date_error')),
          npv: yup
            .number()
            .nullable()
            .transform((value, originalValue) =>
              originalValue === '' ||
              originalValue === null ||
              originalValue === undefined
                ? null
                : value,
            ),
        })
        .required(),
    [],
  )

  const formRef = useRef<HTMLFormElement | null>(null)

  const { control, isValid, getValues, onTriggerChange, onFormSubmit, watch } =
    useForm<any>({
      isEditing,
      defaultValues: initialData,
      schema,
      onSubmit,
    })

  const { dealStatusOptions } = useSelect()

  useEffect(() => {
    if (initialData?.customer) {
      onTriggerChange('customer', initialData.customer.id)
    }
  }, [initialData?.customer, onTriggerChange])

  useEffect(() => {
    if (initialData?.proposal) {
      onTriggerChange('proposal', initialData?.proposal?.id)
    }
  }, [initialData?.proposal, onTriggerChange])

  useEffect(() => {
    if (initialData?.car) {
      onTriggerChange('car', initialData.car.id)
    }
  }, [initialData?.car, onTriggerChange])

  useEffect(() => {
    if (initialData?.agent) {
      onTriggerChange('agent', initialData.agent.id)
    }
  }, [initialData?.agent, onTriggerChange])

  const emptyValue = useMemo(() => (isEditing ? '' : '...'), [isEditing])

  const onSyncFields = useCallback(
    (f: string, v: any) => {
      if (f === 'startDate') {
        const endDate = getValues('endDate')
        if (endDate) {
          onTriggerChange(
            'leaseDuration',
            moment(endDate, 'YYYY-MM-DD').diff(v, 'days'),
          )
        }
      } else if (f === 'endDate') {
        const startDate = getValues('startDate')
        if (startDate) {
          onTriggerChange(
            'leaseDuration',
            moment(v).diff(moment(startDate, 'YYYY-MM-DD'), 'days'),
          )
        }
      } else if (!isNaN(+v) && f === 'leaseDuration') {
        const startDate = getValues('startDate')
        if (startDate) {
          onTriggerChange(
            'endDate',
            moment(startDate, 'YYYY-MM-DD')
              .add(+v, 'days')
              .format('YYYY-MM-DD'),
          )
        }
      }
    },
    [getValues, onTriggerChange],
  )
  const buildData = useBuildData(initialData as any)

  const dealFields = useMemo(
    () => (initialData ? buildData : undefined),
    [buildData, initialData],
  )

  return (
    <>
      <div className="main-info">
        <div className="close-box">
          <Button onClick={onCancel}>
            <span className="icon-close"></span>
          </Button>
        </div>
        <div className="section-box">
          <div className="toolbox">
            <div className="title-box">
              <h4>{t('edit')}</h4>
            </div>
          </div>

          <div className="edit-data">
            <Form<IDeal>
              formRef={formRef}
              control={control}
              isEditing={isEditing}
              onSubmit={onFormSubmit}
            >
              <div className="form-fields">
                {Object.keys(dealFields || {}).map((key, index) => {
                  if (key === 'financial_details') {
                    return (
                      <React.Fragment key={index}>
                        <ProposalPriceFormComponents
                          initialData={initialData as any}
                          onTriggerChange={onTriggerChange}
                          isFormElement
                          control={control}
                          emptyValue={emptyValue}
                          getValues={getValues}
                          watch={watch}
                          isEditing={isEditing}
                        />
                        <TextInput
                          name="npv"
                          type="number"
                          control={control}
                          label={t('npv')}
                          emptyValue={emptyValue}
                          disabled={!Boolean(isEditing)}
                        />
                      </React.Fragment>
                    )
                  }
                  // @ts-ignore
                  return dealFields[key].map(field => {
                    if (!field.name || field.editable === false) return null
                    return (
                      <TextInput
                        key={field.name}
                        name={field.name}
                        control={control}
                        label={t(field.label)}
                        emptyValue={emptyValue}
                        className={field.className}
                        disabled={!Boolean(isEditing) || field.disabled}
                        onFieldChange={d => onSyncFields(field.name, d)}
                      />
                    )
                  })
                })}
                <CustomerSelector
                  isFormElement
                  className="w-50"
                  control={control}
                  isEditing={isEditing}
                  initial={initialData?.customer}
                  selected={getValues('customer')}
                />
                <OptionSelector
                  required
                  name="status"
                  className="w-50"
                  control={control}
                  label={t('status')}
                  emptyValue={emptyValue}
                  options={dealStatusOptions}
                  disabled={!Boolean(isEditing)}
                />
                <CarSelector
                  isFormElement
                  className="w-50"
                  control={control}
                  isEditing={isEditing}
                  initial={initialData?.car}
                  selected={getValues('car')}
                />
                <CarSubModelSelector
                  isFormElement
                  className="w-50"
                  control={control}
                  loading={loading}
                  isEditing={isEditing}
                  onTriggerChange={onTriggerChange}
                  initialSubModel={initialData?.carSubModel}
                  initialModel={initialData?.carSubModel?.carModel}
                  initialBrand={initialData?.carSubModel?.carModel?.brand}
                />
                <DatePicker
                  required
                  className="w-50"
                  name="startDate"
                  control={control}
                  emptyValue={emptyValue}
                  dateFormat="yyyy-MM-dd"
                  label={t('start_time')}
                  disabled={!Boolean(isEditing)}
                  onFieldChange={d => onSyncFields('startDate', d)}
                />
                <DatePicker
                  required
                  className="w-50"
                  name="endDate"
                  control={control}
                  emptyValue={emptyValue}
                  dateFormat="yyyy-MM-dd"
                  label={t('end_time')}
                  disabled={!Boolean(isEditing)}
                  onFieldChange={d => onSyncFields('endDate', d)}
                />
              </div>
            </Form>
          </div>
        </div>
      </div>
      <FormButtons
        formRef={formRef}
        visible={isEditing}
        onCancel={onCancel}
        isFormValid={isValid}
        submitting={submitting}
        cancelClassName={'gray'}
      />
    </>
  )
}

export default DealForm
